import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/lib/auth/authStore";
import { getVisibleNavItems } from "@/app/layouts/sidebarNavConfig";
import { useCapabilities } from "@/features/capabilities/hooks/useCapabilities";
import { formatNavBadgeCount, useNavBadges } from "@/features/dashboard/hooks/useNavBadges";

// Map Albanian nav label → i18n key (covers all 3 nav configs)
const NAV_LABEL_KEY: Record<string, string> = {
  "Dashboard":           "nav.dashboard",
  "Dashboard Korporativ": "nav.corporateDashboard",
  "Agjenda":             "nav.professionalPanel",
  "Workbench":           "nav.labAdmin",
  "Kërko Mjekun":        "nav.search",
  "Faqja ime":           "nav.patientHome",
  "Paneli im":           "nav.professionalPanel",
  "Terminet e Mia":      "nav.appointments",
  "Oraret":              "nav.appointments",
  "Monitorimi Im":       "nav.monitoring",
  "Monitorimi":          "nav.monitoring",
  "Familja Ime":         "nav.familyCare",
  "Kujdesi familjar":    "nav.familyCare",
  "Historiku Klinik":    "nav.clinicalHistory",
  "Laboratori":          "nav.laboratory",
  "Rezultatet":          "nav.labResults",
  "Imazheritë":          "nav.laboratory",
  "Panel Laborantit":    "nav.labAdmin",
  "Service Hub":         "nav.serviceHub",
  "Inventari":           "nav.inventory",
  "Ofertat e Punës":     "nav.jobs",
  "Sigurimet":           "nav.insurance",
  "Ngjarjet":            "nav.events",
  "Favoritet":           "nav.favorites",
  "Wallet":              "nav.wallet",
  "Portofoli":           "nav.businessWallet",
  "Medimarket":          "nav.medimarket",
  "Shporta":             "nav.cart",
  "Dyqani im":           "nav.seller",
  "Regjistro Biznesin":  "nav.registerBusiness",
  "Blogu im":            "nav.myBlog",
  "Moderimi i Blogut":   "nav.postModeration",
  "Faturat":             "nav.invoices",
  "Mesazhet":            "nav.messages",
  "Departamentet":       "nav.departments",
  "Shtretërit":          "nav.hospitalBeds",
  "Pranimet":            "nav.hospitalAdmissions",
  "Control Room":        "nav.admin",
  "Verifikimi i Mjekëve": "nav.verifyDoctors",
  "Menaxhimi i Reklamave": "nav.adsManagement",
  "Verifikimi Mjekëve":  "nav.admin",
  "Siguria":             "nav.admin",
  "Statistikat":         "nav.admin",
  "Module Toggles":      "nav.serviceHub",
  "Platform Config":     "nav.account",
  "Admin":               "nav.admin",
  "Llogaria":            "nav.account",
  "Bizneset":            "nav.businesses",
  "Klinikat":            "nav.businesses",
  "Klinika Ime":         "nav.myClinic",
  "Template i Raportit": "nav.reportTemplate",
};

export interface SidebarNavProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

const exactOnlyPaths = new Set(["/app/dashboard", "/app/superadmin"]);

const getActiveMatchScore = (itemTarget: string, currentPathname: string, currentSearch: string): number => {
  const itemUrl = new URL(itemTarget, "http://app.local");
  const itemPathname = itemUrl.pathname.replace(/\/+$/, "") || "/";
  const normalizedCurrentPathname = currentPathname.replace(/\/+$/, "") || "/";
  const itemSearchParams = itemUrl.searchParams;
  const hasTargetSearch = Array.from(itemSearchParams.keys()).length > 0;

  if (itemPathname !== normalizedCurrentPathname) {
    if (exactOnlyPaths.has(itemPathname)) {
      return -1;
    }
    if (!normalizedCurrentPathname.startsWith(`${itemPathname}/`)) {
      return -1;
    }
  }

  if (hasTargetSearch) {
    const currentSearchParams = new URLSearchParams(currentSearch);
    for (const [key, value] of itemSearchParams.entries()) {
      if (currentSearchParams.get(key) !== value) {
        return -1;
      }
    }
    return 3;
  }

  if (exactOnlyPaths.has(itemPathname) && currentSearch) {
    return -1;
  }

  return itemPathname === normalizedCurrentPathname ? 2 : 1;
};

export const SidebarNav = ({ collapsed = false, onNavigate }: SidebarNavProps): JSX.Element => {
  const { systemRole, profileRole, businessRole, activeFunctionalAccount } = useAuthStore();
  const capabilitiesQuery = useCapabilities();
  const { pathname, search } = useLocation();
  const currentLevel = capabilitiesQuery.data?.institution_level ?? 1;
  const visibleItems = getVisibleNavItems(
    systemRole,
    profileRole,
    businessRole,
    capabilitiesQuery.data,
    pathname,
    currentLevel,
    activeFunctionalAccount?.functional_account_type ?? null,
  );
  const navBadgesQuery = useNavBadges(visibleItems.some((item) => Boolean(item.badgeKey)));
  const navBadgeCounts = navBadgesQuery.data?.counts ?? {};
  const { t } = useTranslation();
  const activeItemIndex = visibleItems.reduce(
    (bestMatch, item, index) => {
      const score = getActiveMatchScore(item.to, pathname, search);
      return score > bestMatch.score ? { index, score } : bestMatch;
    },
    { index: -1, score: -1 },
  ).index;

  return (
    <nav className="space-y-1" aria-label="Navigimi kryesor">
      {visibleItems.map((item, index) => {
        const labelKey = NAV_LABEL_KEY[item.label];
        const translatedLabel = labelKey ? t(labelKey, { defaultValue: item.label }) : item.label;
        const badgeCount = item.badgeKey ? Number(navBadgeCounts[item.badgeKey] ?? 0) : 0;
        const isActive = index === activeItemIndex;
        return (
          <Link
            key={`${item.to}:${item.label}`}
            to={item.to}
            className={cn(
              "relative flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground",
              isActive && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              collapsed && "justify-center px-0",
            )}
            aria-current={isActive ? "page" : undefined}
            title={collapsed ? translatedLabel : undefined}
            onClick={onNavigate}
          >
            {item.icon}
            {collapsed ? null : <span className="truncate">{translatedLabel}</span>}
            {badgeCount > 0 ? (
              <span
                className={cn(
                  "ml-auto inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold leading-5 text-primary-foreground ring-1 ring-primary/20",
                  collapsed && "absolute -right-1 -top-1 ml-0",
                )}
                aria-label={`${formatNavBadgeCount(badgeCount)} te reja`}
              >
                {formatNavBadgeCount(badgeCount)}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
};
